import React from 'react';
import { fmtHM } from '../lib/format.js';
import { Icon } from './Icon.jsx';

// Stored sources are tagged with a `<system>:<id>` prefix (e.g.
// `calendar:abc123`, `jira:PAY-412:2026-05-08`, `calamari:9921:2026-05-08`).
// The badge surfaces *where* an entry came from at a glance; the exact id
// is implementation detail. `calamari:*` falls back to the manual icon
// per product call — we don't expose Calamari's identity in the UI.
export function sourceKind(raw) {
  const s = String(raw || '').toLowerCase();
  if (s === 'copy') return 'copy';
  if (s.startsWith('calendar:')) return 'calendar';
  if (s.startsWith('jira:')) return 'jira';
  return 'manual';
}

const SOURCE_META = {
  manual: {
    icon: 'edit',
    color: 'var(--sp-fg-3)',
    bg: 'var(--sp-bg-muted)',
    label: 'Logged manually',
  },
  calendar: {
    icon: 'cal',
    color: '#007C7C',
    bg: 'var(--sp-mint-100)',
    label: 'Imported from Calendar',
  },
  jira: {
    icon: 'jiraMark',
    color: '#0052CC',
    bg: '#E9F2FF',
    label: 'Imported from Jira',
  },
  copy: {
    icon: 'repeat',
    color: '#B57651',
    bg: '#FFF1E1',
    label: 'Copied from a previous week',
  },
};

function SourceBadge({ source }) {
  const meta = SOURCE_META[sourceKind(source)];
  return (
    <span
      title={meta.label}
      aria-label={meta.label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        borderRadius: 5,
        flexShrink: 0,
        background: meta.bg,
        color: meta.color,
      }}
    >
      <Icon name={meta.icon} size={11} stroke={2.2} />
    </span>
  );
}

function ProductBadge({ product }) {
  if (!product || product === 'None') return null;
  const colors =
    product === 'SwiftPay'
      ? { bg: '#E6FDF9', fg: '#007C7C' }
      : { bg: '#FFE3D3', fg: '#B57651' };
  return (
    <span
      style={{
        background: colors.bg,
        color: colors.fg,
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 12,
        letterSpacing: 0.1,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      {product}
    </span>
  );
}

export function EntryRow({ entry, onEdit, onDelete, onView, locked = false }) {
  const mins = entry.hours * 60 + entry.minutes;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '88px 1fr auto',
        alignItems: 'center',
        gap: 16,
        padding: '14px 16px',
        borderRadius: 12,
        transition: 'background .12s',
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.background = 'var(--sp-bg-page)')
      }
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.01em',
        }}
      >
        {fmtHM(mins)}
      </div>

      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{ fontWeight: 600, fontSize: 14, color: 'var(--sp-fg-1)' }}
          >
            {entry.activityName}
          </span>
          <SourceBadge source={entry.source} />
          <ProductBadge product={entry.product} />
          {entry.ticketId && (
            <span
              title={`Ticket ${entry.ticketId}${entry.ticketTitle ? ` — ${entry.ticketTitle}` : ''}`}
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#5E2A9C',
                background: '#F2EAFE',
                padding: '2px 6px',
                borderRadius: 4,
                fontFamily: 'DM Sans',
              }}
            >
              {entry.ticketId}
            </span>
          )}
          {entry.epicId && (
            <span
              title={entry.ticketId
                ? `Epic ${entry.epicId}${entry.epicTitle ? ` — ${entry.epicTitle} (parent of ${entry.ticketId})` : ''}`
                : `Epic ${entry.epicId}${entry.epicTitle ? ` — ${entry.epicTitle}` : ''}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--sp-fg-2)',
                background: 'var(--sp-bg-muted)',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              {entry.ticketId && (
                <Icon name="flow" size={10} stroke={2} />
              )}
              {entry.epicId}
            </span>
          )}
          {entry.clientName && (
            <span style={{ fontSize: 12, color: 'var(--sp-fg-3)' }}>
              · {entry.clientName}
            </span>
          )}
        </div>
        {(entry.description || entry.ticketTitle || entry.epicTitle) && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--sp-fg-3)',
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {entry.description || entry.ticketTitle || entry.epicTitle}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 4, opacity: 0.85 }}>
        {locked ? (
          // v2.0 (A6 / drill-in) — read-only state. Edit + Delete are
          // hidden (not greyed) to reduce noise; a single info icon
          // surfaces the read-only inspector for description / metadata.
          onView && (
            <button
              className="btn btn-ghost btn-icon"
              title="View details"
              onClick={() => onView(entry)}
            >
              <Icon name="info" size={15} />
            </button>
          )
        ) : (
          <>
            {onEdit && (
              <button
                className="btn btn-ghost btn-icon"
                title="Edit"
                onClick={() => onEdit(entry)}
              >
                <Icon name="edit" size={15} />
              </button>
            )}
            {onDelete && (
              <button
                className="btn btn-ghost btn-icon"
                title="Delete"
                onClick={() => onDelete(entry)}
              >
                <Icon name="trash" size={15} />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
